import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

import { SortableContext, arrayMove } from '@dnd-kit/sortable'
import { createPortal } from 'react-dom'

import { BlockContent as BlockContentType } from '~/bootcamp/Activities/types/activities.types'

import { BlockContent } from '~/components'

import { getColumnsData, getTasksData, taskCardText } from '../types/Categorization.data'

import { CategorizationAnswerType, Column, Task, TaskStatus } from '../types/Categorization.type'

import TaskCard from './TaskCard'

import ColumnContainer from './ColumnContainer'

interface QuestionBoardProps {
  question: BlockContentType[]
  setFeedBackMessage: (feedback: CategorizationAnswerType | null) => void
  taskStatus: TaskStatus
  setTaskStatus: (status: TaskStatus) => void
  /** The card currently on top of the pool, or null when it is empty. */
  onVisibleItemChange?: (item: string | null) => void
}

function CategorizationApp({
  setFeedBackMessage,
  question,
  taskStatus,
  setTaskStatus,
  onVisibleItemChange,
}: QuestionBoardProps): React.ReactElement {
  const [columns, setColumns] = useState<Column[]>(getColumnsData())
  const [tasks, setTasks] = useState<Task[]>(getTasksData())
  const [activeColumn, setActiveColumn] = useState<Column | null>(null)
  const [activeTask, setActiveTask] = useState<Task | null>(null)

  // Ref to track last drag over state to prevent unnecessary updates
  const lastDragOverRef = useRef<{
    activeId: string | number
    overId: string | number
    columnId: string | number
  } | null>(null)
  const dragOverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const columnsId = useMemo(() => columns.map((col) => col.id), [columns])

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  )

  const questionCol = {
    id: 'questions',
    categoryName: 'Questions',
  }

  const readyToCheck = tasks.filter((task) => task.columnId === questionCol.id).length < 1

  // Reported as text, never `task.category` (the answer key).
  const firstPoolTask = tasks.find((task) => task.columnId === questionCol.id)
  const visibleItem = firstPoolTask ? taskCardText(firstPoolTask.content) || null : null
  useEffect(() => {
    onVisibleItemChange?.(visibleItem)
  }, [visibleItem, onVisibleItemChange])

  useEffect(() => {
    if (readyToCheck && taskStatus === TaskStatus.Unchecked) {
      setTaskStatus(TaskStatus.ReadyToCheck)
    }

    if (taskStatus === TaskStatus.Checking) {
      tasks.map((task): Task => {
        if (task.category === task.columnId) task.taskStatus = TaskStatus.Right
        else task.taskStatus = TaskStatus.Wrong
        task.check = true
        return task
      })

      const needRetry = tasks.filter((task) => task.taskStatus !== TaskStatus.Right).length > 0
      if (needRetry) setTaskStatus(TaskStatus.Wrong)
      else setTaskStatus(TaskStatus.Right)
      setFeedBackMessage(needRetry ? CategorizationAnswerType.SELECT_ALL_CORRECT : CategorizationAnswerType.CORRECT)
    } else if (taskStatus === TaskStatus.Reset) {
      setTaskStatus(TaskStatus.Checking)
      tasks.map((task): Task => {
        task.check = false
        task.columnId = questionCol.id
        task.taskStatus = TaskStatus.Unchecked
        return task
      })

      setTasks(
        // eslint-disable-next-line fp/no-mutating-methods
        tasks
          .map((value: Task) => ({ value, sort: Math.random() }))
          .sort((a: { sort: number }, b: { sort: number }) => a.sort - b.sort)
          .map(({ value }: { value: Task }) => value),
      )
      setTaskStatus(TaskStatus.Unchecked)
      setFeedBackMessage(null)
    }
  }, [questionCol.id, readyToCheck, setFeedBackMessage, setTaskStatus, taskStatus, tasks])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dragOverTimeoutRef.current) {
        clearTimeout(dragOverTimeoutRef.current)
      }
    }
  }, [])

  const onDragOver = useCallback((event: DragOverEvent): void => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id
    const overId = over.id

    if (activeId === overId) return

    const isActiveATask = active.data.current?.type === 'Task'
    const isOverATask = over.data.current?.type === 'Task'

    if (!isActiveATask) return

    // Clear any existing timeout
    if (dragOverTimeoutRef.current) {
      clearTimeout(dragOverTimeoutRef.current)
    }

    // Debounce the drag over updates to prevent rapid state changes
    dragOverTimeoutRef.current = setTimeout(() => {
      if (isActiveATask && isOverATask) {
        setTasks((e) => {
          const activeIndex = e.findIndex((t) => t.id === activeId)
          const overIndex = e.findIndex((t) => t.id === overId)

          if (e[activeIndex].columnId !== e[overIndex].columnId) {
            e[activeIndex].columnId = e[overIndex].columnId
            return arrayMove(e, activeIndex, overIndex - 1)
          }
          return arrayMove(e, activeIndex, overIndex)
        })
      }

      const isOverAColumn = over.data.current?.type === 'Column'

      if (isActiveATask && isOverAColumn) {
        setTasks((e) => {
          const activeIndex = e.findIndex((t) => t.id === activeId)

          e[activeIndex].columnId = overId
          const movedTasks = arrayMove(e, activeIndex, activeIndex)
          return movedTasks
        })
      }
    }, 50)
  }, [])

  return (
    <div>
      <div className="m-auto min-h-132 w-full overflow-hidden">
        <h2 className="text-black mb-4 text-4 text-center">
          <BlockContent blocks={question} />
        </h2>

        <DndContext onDragEnd={onDragEnd} onDragOver={onDragOver} onDragStart={onDragStart} sensors={sensors}>
          <div className="flex gap-4">
            <div className="flex gap-2 md:gap-4 w-full touch-none">
              <SortableContext items={columnsId}>
                {columns.map((col) => (
                  <div className="w-1/2" key={col.id}>
                    <ColumnContainer
                      column={col}
                      key={col.id}
                      tasks={tasks.filter((task) => task.columnId === col.id)}
                    />
                  </div>
                ))}
              </SortableContext>
            </div>
          </div>

          <div className={`${readyToCheck ? 'hidden' : 'flex gap-4 max-h-32 w-1/2 mx-auto pt-4 touch-none'}`}>
            <div className="w-full">
              <SortableContext items={columnsId}>
                <ColumnContainer
                  column={questionCol}
                  key={questionCol.id}
                  needTitle={false}
                  tasks={tasks.filter((task) => task.columnId === 'questions')}
                />
              </SortableContext>
            </div>
          </div>
          {/* The overlay is portaled to document.body with dnd-kit's default z-index of 999, which the
              activity modal (z-modal-over-topbar, 100000) paints over - z-modal-top (99999) is still
              under it, so the dragged card needs the top of the scale. */}
          {typeof document !== 'undefined' &&
            createPortal(
              <DragOverlay className="!z-top">
                {activeColumn && (
                  <ColumnContainer
                    column={activeColumn}
                    tasks={tasks.filter((task) => task.columnId === activeColumn.id)}
                  />
                )}
                {activeTask && <TaskCard task={activeTask} />}
              </DragOverlay>,
              document.body,
            )}
        </DndContext>
      </div>
    </div>
  )

  function onDragStart(event: DragStartEvent): void {
    if (event.active.data.current?.type === 'Column') {
      setActiveColumn(event.active.data.current.column)
      return
    }

    if (event.active.data.current?.type === 'Task') {
      setActiveTask(event.active.data.current.task)
      return
    }
  }

  function onDragEnd(event: DragEndEvent): void {
    setActiveColumn(null)
    setActiveTask(null)

    // // Clear any pending drag over timeout and reset ref
    if (dragOverTimeoutRef.current) {
      clearTimeout(dragOverTimeoutRef.current)
      dragOverTimeoutRef.current = null
    }
    lastDragOverRef.current = null

    const { active, over } = event
    if (!over) return

    const activeId = active.id
    const overId = over.id

    if (activeId === overId) return

    const isActiveAColumn = active.data.current?.type === 'Column'
    if (!isActiveAColumn) return

    setColumns((cols) => {
      const activeColumnIndex = cols.findIndex((col) => col.id === activeId)

      const overColumnIndex = cols.findIndex((col) => col.id === overId)

      return arrayMove(cols, activeColumnIndex, overColumnIndex)
    })
  }
}

export default CategorizationApp
